import React from "react";
import axios, { AxiosError } from "axios";
import _uniqBy from "lodash/uniqBy";
import { LDFlagSet } from "launchdarkly-react-client-sdk";
import {
  AccountType,
  getErrorPopupTexts,
  ICheckboxGroupValue,
  isCheckboxGroupValueValid,
  RouterProps,
} from "component-library";
import { LaunchDarklyClientWithFlags } from "shared-services";

import { filterFacets } from "shared/helpers/facetHelpers";
import {
  mapFacetCheckboxGroup,
  mapFacetGroupedOptions,
} from "shared/helpers/mappers";
import { IFacetGroup } from "shared/models/facet";
import {
  IAgentJobSettings,
  IPerformerJobSettings,
} from "shared/models/settings";
import ExpiringUserStorageService from "shared/services/api/localStorage/ExpiringUserStorageService";
import localStorageService from "shared/services/api/localStorage/localStorageService";
import scrollManager from "shared/services/scrollManager";
import { Diff, ValueOf } from "shared/types/utilities";

import { JOB_UPDATE_NAVIGATION_DELAY } from "config/common";

import { HidePopup, PopupType, ShowPopup } from "store/ui/ui.types";

import { IWithRequestAbortController } from "types/axios";

import { IJobFilterParams } from "models/api";
import { IPaginatedData } from "models/common";
import { IJobItem, IPaginatedJobItemList } from "models/job";

import getErrorPopupType from "helpers/getErrorPopupType";
import {
  mapCheckboxGroupValueToNumericValues,
  mapNumericOptionsToCheckboxGroupValue,
} from "helpers/mappers";
import {
  IInteractionItem,
  SetJobActionType,
} from "services/api/job/jobService";

import {
  DEFAULT_PAGE_NUMBER,
  DEFAULT_VALUES,
  FilterControlType,
  FilterType,
  IFilterConfig,
  IFilterModel,
  ShowType,
} from "hoc/withJobsFilter/withJobsFilter.config";
import {
  FilterModelToJobFilterParamsConverter,
  JobTableItemUuidToInteractionItemConverter,
} from "hoc/withJobsFilter/withJobsFilter.converters";

import {
  IJobsPageCommonTexts,
  IJobsPageInjectedProps,
} from "pages/JobsPage/JobsPage";
import { JobsPageTab } from "pages/JobsPage/JobsPage.config";

import JobsFilterControl from "components/JobsFilterControl/JobsFilterControl";
import {
  IJobTableItem,
  JobTableItemUuid,
} from "components/JobsTable/JobsTable.models";

export type ChangeFilters = (
  values: Partial<IFilterModel>,
  callback?: (updatedModel: IFilterModel) => void
) => void;

export type AddFilterValue = (
  filterType: FilterType,
  value: ValueOf<IFilterModel>
) => void;

export type CustomFacetFilter = (
  facetGroups: IFacetGroup[],
  userType?: AccountType
) => IFacetGroup[];

export interface IWithJobsFilterInjectedProps {
  filterConfig: IFilterConfig;
  changeFilters: ChangeFilters;
  addFilterValue: AddFilterValue;
  filterValues: IFilterModel;
  renderJobsFilterControl(): React.ReactNode;
  isLoading: boolean;
  paginatedItems: IPaginatedData<IJobTableItem>;
  onMarkAsRead(itemIds: JobTableItemUuid[]): Promise<void>;
  onMarkAsUnread(itemIds: JobTableItemUuid[]): Promise<void>;
}

export interface IWithJobsFilterProps
  extends
    IWithJobsFilterInjectedProps,
    IJobsPageInjectedProps,
    LaunchDarklyClientWithFlags,
    RouterProps {
  showPopup: ShowPopup;
  hidePopup: HidePopup;
  convertToTableItem: (
    jobItem: IJobItem,
    ldFlags?: LDFlagSet,
    userType?: AccountType
  ) => IJobTableItem;
  findJobs(
    params: IJobFilterParams,
    abortController?: AbortController
  ): Promise<IPaginatedJobItemList>;
  userType: AccountType;
  userId: string;
  markAsRead: SetJobActionType;
  markAsUnread: SetJobActionType;
}

export interface IWithJobsFilterState {
  filterConfig: IFilterConfig;
  values: IFilterModel;
  isLoading: boolean;
  paginatedJobs: IPaginatedData<IJobItem>;
}

export interface IWithJobsFilterOptions {
  tab: JobsPageTab;
  applyUserSettings?: boolean;
  filterContractTypes?: CustomFacetFilter;
}

type HocProps<TProps extends object> = Diff<
  TProps,
  IWithJobsFilterInjectedProps
>;

export interface ISetupFilterConfigParams extends Omit<
  IWithJobsFilterOptions,
  "tab"
> {
  commonTexts: IJobsPageCommonTexts;
  productionTypes: IFacetGroup[];
  contractTypes: IFacetGroup[];
  jobSettings: IPerformerJobSettings | IAgentJobSettings;
  userType: AccountType;
}

export function filterFacetGroups(
  facetGroups: IFacetGroup[],
  shouldUseAny: boolean,
  settings: number[],
  applyUserSettings?: boolean,
  customFilter?: CustomFacetFilter,
  userType?: AccountType
): IFacetGroup[] {
  if (applyUserSettings) {
    return shouldUseAny ? facetGroups : filterFacets(facetGroups, settings);
  } else {
    return customFilter?.(facetGroups, userType) ?? facetGroups;
  }
}

export function setupFilterConfig(
  params: ISetupFilterConfigParams
): IFilterConfig {
  const {
    commonTexts,
    productionTypes,
    contractTypes,
    jobSettings,
    userType,
    applyUserSettings,
    filterContractTypes,
  } = params;
  const filteredProductionTypes: IFacetGroup[] =
    !params.applyUserSettings || jobSettings?.anyProductionType
      ? productionTypes
      : filterFacets(productionTypes, jobSettings.productionTypes);
  const filteredContractTypes: IFacetGroup[] = filterFacetGroups(
    contractTypes,
    jobSettings?.anyContractType,
    jobSettings?.contractTypes,
    applyUserSettings,
    filterContractTypes,
    userType
  );

  return {
    [FilterType.ProductionType]: {
      translations: commonTexts.placeholders.productionType,
      defaultValue: {},
      filterControlType: FilterControlType.Dropdown,
      drawerFilterControlType: FilterControlType.CheckboxGroup,
      facets: filteredProductionTypes,
      dropdownOptions: mapFacetGroupedOptions(filteredProductionTypes),
      checkboxOptions: mapFacetCheckboxGroup(filteredProductionTypes, {
        sortFacets: true,
      }),
    },
    [FilterType.ContractType]: {
      translations: commonTexts.placeholders.contractType,
      defaultValue: {},
      filterControlType: FilterControlType.Dropdown,
      drawerFilterControlType: FilterControlType.CheckboxGroup,
      facets: filteredContractTypes,
      dropdownOptions: mapFacetGroupedOptions(filteredContractTypes),
      checkboxOptions: mapFacetCheckboxGroup(filteredContractTypes, {
        sortFacets: true,
      }),
    },
    [FilterType.DateSent]: {
      translations: commonTexts.placeholders.dateSent,
      defaultValue: undefined,
      filterControlType: FilterControlType.DatePicker,
      drawerFilterControlType: FilterControlType.DatePicker,
    },
    [FilterType.Sender]: {
      translations: commonTexts.placeholders.sender,
      defaultValue: undefined,
    },
    [FilterType.PlayingAgeMin]: {
      translations: commonTexts.placeholders.playingAge,
      defaultValue: undefined,
    },
    [FilterType.PlayingAgeMax]: {
      translations: "",
      defaultValue: undefined,
    },
    [FilterType.Locations]: {
      translations: {
        locationLabel: commonTexts.placeholders.locationLabel,
        searchLocationLabel: commonTexts.placeholders.searchLocationLabel,
        searchLocationPlaceholder:
          commonTexts.placeholders.searchLocationPlaceholder,
      },
      defaultValue: [],
      filterControlType: FilterControlType.Locations,
      drawerFilterControlType: FilterControlType.Locations,
    },
  };
}

export default function withJobsFilterFactory<
  TProps extends IWithJobsFilterProps,
>(options: IWithJobsFilterOptions) {
  return (
    WrappedComponent: React.ComponentType<TProps>
  ): React.ComponentType<HocProps<TProps>> => {
    class WithJobsFilter
      extends React.Component<HocProps<TProps>, IWithJobsFilterState>
      implements IWithRequestAbortController
    {
      public abortController = new AbortController();
      private storageKey = `filter:${options.tab}`;
      private storageService = new ExpiringUserStorageService({
        storage: localStorageService,
        ttl: 1000 * 60 * 60 * 12,
        userId: this.props.userId,
      });

      constructor(props: HocProps<TProps>) {
        super(props);
        const filterConfig = this.setupFilterConfig();

        this.state = {
          filterConfig,
          values: this.generateDefaultValues(
            filterConfig,
            props.userType,
            props.restoreScrollPosition,
            props.page
          ),
          isLoading: false,
          paginatedJobs: {
            count: 0,
            items: [],
          },
        };
      }

      public componentDidMount() {
        void this.loadJobs(true);
      }

      public componentDidUpdate(prevProps: HocProps<TProps>) {
        const propsPageNumber = this.props.page;
        const statePageNumber = this.state.values[FilterType.PageNumber];

        if (
          this.props.pageTranslationLocale !==
            prevProps.pageTranslationLocale ||
          this.props.facetLocale !== prevProps.facetLocale
        ) {
          this.setState({ filterConfig: this.setupFilterConfig() });
        }

        if (
          propsPageNumber !== prevProps.page &&
          propsPageNumber !== statePageNumber
        ) {
          this.changeFilters({ [FilterType.PageNumber]: propsPageNumber });
        } else if (propsPageNumber !== statePageNumber) {
          const { router } = this.props;
          const [, setSearchParams] = router.searchParams;
          setSearchParams();
        }
      }

      public componentWillUnmount() {
        this.abortController.abort?.();
      }

      public render() {
        const { paginatedJobs } = this.state;

        return (
          <WrappedComponent
            {...(this.props as TProps)}
            filterConfig={this.state.filterConfig}
            changeFilters={this.changeFilters}
            addFilterValue={this.addFilterValue}
            filterValues={this.state.values}
            paginatedItems={{
              count: paginatedJobs?.count ?? 0,
              items:
                paginatedJobs?.items.map((item) =>
                  this.props.convertToTableItem(
                    item,
                    this.props.flags,
                    this.props.userType
                  )
                ) ?? [],
            }}
            isLoading={this.state.isLoading}
            renderJobsFilterControl={this.renderControl}
            onMarkAsRead={this.handleMarkAsRead}
            onMarkAsUnread={this.handleMarkAsUnread}
          />
        );
      }

      public renderControl = () => {
        const { locale, productionTypes, contractTypes, commonTexts } =
          this.props;
        const { filterConfig, values } = this.state;

        return (
          <JobsFilterControl
            locale={locale}
            productionTypes={productionTypes}
            contractTypes={contractTypes}
            placeholders={commonTexts.placeholders}
            changeFilters={this.changeFilters}
            filterValues={values}
            filterConfig={filterConfig}
          />
        );
      };

      private changeFilters: ChangeFilters = (values, callback) => {
        const updatedValues = {
          ...this.state.values,
          [FilterType.PageNumber]: DEFAULT_PAGE_NUMBER,
          ...values,
        };

        this.setState({ values: updatedValues }, () => {
          callback?.(updatedValues);
          void this.loadJobs();
          this.preserveFilters(updatedValues);
        });
      };

      private addFilterValue: AddFilterValue = (filterType, value) => {
        const { values, filterConfig } = this.state;
        const { drawerFilterControlType, checkboxOptions } =
          filterConfig[filterType];
        let updatedValue;

        if (filterType === FilterType.Locations && Array.isArray(value)) {
          const locationFilters = values[filterType];
          updatedValue = _uniqBy([...value, ...locationFilters], "value");
        }

        if (
          drawerFilterControlType === FilterControlType.CheckboxGroup &&
          value !== undefined
        ) {
          updatedValue = mapNumericOptionsToCheckboxGroupValue(
            checkboxOptions,
            [
              ...mapCheckboxGroupValueToNumericValues(
                values[filterType] as ICheckboxGroupValue
              ),
              value as number,
            ]
          );
        }

        this.changeFilters({ [filterType]: updatedValue });
      };

      private async loadJobs(isInitial?: boolean) {
        const { userType, jobSettings, restoreScrollPosition } = this.props;
        const { values } = this.state;

        this.setState({ isLoading: true });
        this.abortController.abort?.();
        this.abortController = new AbortController();
        try {
          const converter = new FilterModelToJobFilterParamsConverter({
            contractTypes:
              this.state.filterConfig[FilterType.ContractType].facets!,
            productionTypes:
              this.state.filterConfig[FilterType.ProductionType].facets!,
            userType,
            jobSettings: options?.applyUserSettings ? jobSettings : undefined,
          });
          const paginatedJobs = await this.props.findJobs(
            converter.convert(values),
            this.abortController
          );

          this.setState({ paginatedJobs }, () => {
            if (isInitial && restoreScrollPosition) {
              scrollManager.scrollTo(restoreScrollPosition, 0);
            }
          });
        } catch (e) {
          this.handleError(e as AxiosError);
        } finally {
          this.setState({ isLoading: false });
        }
      }

      private handleMarkAsRead = async (itemIds: JobTableItemUuid[]) => {
        await this.toggleReadStatus(itemIds, this.props.markAsRead);
      };

      private handleMarkAsUnread = async (itemIds: JobTableItemUuid[]) => {
        await this.toggleReadStatus(itemIds, this.props.markAsUnread);
      };

      private async toggleReadStatus(
        itemIds: JobTableItemUuid[],
        handler: SetJobActionType
      ) {
        const converter = new JobTableItemUuidToInteractionItemConverter();
        const interactions = itemIds.map<IInteractionItem>((id) =>
          converter.convert(id)
        );
        this.setState({ isLoading: true });

        try {
          await handler(interactions);
          setTimeout(() => {
            this.changeFilters(
              {
                [FilterType.PageNumber]:
                  this.state.values[FilterType.PageNumber],
              },
              this.props.refreshCounters
            );
          }, JOB_UPDATE_NAVIGATION_DELAY);
        } catch (e) {
          this.handleError(e as AxiosError);
        }
      }

      private handleError(e: AxiosError) {
        if (axios.isCancel(e)) {
          return;
        }

        this.props.showPopup({
          type: PopupType.ErrorPopup,
          data: {
            ...getErrorPopupTexts()[getErrorPopupType()],
            hidePopup: this.props.hidePopup,
          },
        });
      }

      private setupFilterConfig = (): IFilterConfig => {
        const {
          commonTexts,
          productionTypes,
          contractTypes,
          jobSettings,
          userType,
        } = this.props;

        return setupFilterConfig({
          commonTexts,
          productionTypes,
          contractTypes,
          jobSettings,
          userType,
          applyUserSettings: options?.applyUserSettings,
          filterContractTypes: options?.filterContractTypes,
        });
      };

      private preserveFilters(values: IFilterModel) {
        this.storageService.set<IFilterModel>(this.storageKey, values);
      }

      private generateDefaultValues(
        filterConfig: IFilterConfig,
        userType: AccountType,
        restoreScrollPosition: number | null,
        page: number
      ): IFilterModel {
        const values = this.storageService.get<IFilterModel>(this.storageKey);
        const updatedDefaultValues = {
          ...DEFAULT_VALUES,
          [FilterType.ShowType]:
            userType === AccountType.Performer
              ? ShowType.AllRoles
              : ShowType.AllBreakdowns,
        };

        if (!values) {
          return { ...updatedDefaultValues, [FilterType.PageNumber]: page };
        }

        const productionTypesValue = values[FilterType.ProductionType];
        const contractTypesValue = values[FilterType.ContractType];

        return {
          ...updatedDefaultValues,
          ...values,
          [FilterType.ProductionType]:
            productionTypesValue &&
            isCheckboxGroupValueValid(
              {
                name: FilterType.ProductionType,
                options: filterConfig.productionType.checkboxOptions!,
              },
              productionTypesValue
            )
              ? productionTypesValue
              : updatedDefaultValues[FilterType.ProductionType],
          [FilterType.ContractType]:
            contractTypesValue &&
            isCheckboxGroupValueValid(
              {
                name: FilterType.ContractType,
                options: filterConfig.contractType.checkboxOptions!,
              },
              contractTypesValue
            )
              ? contractTypesValue
              : updatedDefaultValues[FilterType.ContractType],
          [FilterType.PageNumber]: page,
          [FilterType.TextFilter]:
            restoreScrollPosition === null
              ? updatedDefaultValues[FilterType.TextFilter]
              : values[FilterType.TextFilter],
        };
      }
    }

    return WithJobsFilter;
  };
}
